import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import DropdownMenu, { DropdownMenuItem } from '../DropdownMenu';

/** 单位¹ 预设（与产品约定一致） */
export const BUDGET_UNIT1_PRESETS = ['个', '人', '台', '辆', '间', '米', '千克'] as const;

/** 单位² 预设 */
export const BUDGET_UNIT2_PRESETS = ['次', '分', '时', '天', '周', '月', '年'] as const;

function toDisplay(stored: string): string {
  if (!stored || stored === '--') return '';
  return stored;
}

function toStored(display: string): string {
  const trimmed = display.trim();
  return trimmed === '' ? '--' : trimmed;
}

export interface BudgetUnitInputProps {
  /** 约定：'--' 表示空，与预算表 handleCellChange 一致 */
  value: string;
  onChange: (value: string) => void;
  presets: readonly string[];
  disabled?: boolean;
  placeholder?: string;
  /** 下拉唯一 id，避免多实例冲突 */
  dropdownId: string;
  /**
   * table：与预算表 EditableCell 相同 class（editable-cell-*，样式来自 BudgetTable.css）
   * form：关联预算创建表单，使用普通 text input，由 .create-form-field 统一样式
   */
  variant?: 'table' | 'form';
  /**
   * 预设下拉与表格折叠态展示翻译；写入 onChange 的仍为单位规范字（与 presets 的 id 一致）。
   */
  translateUnitLabel?: (canonical: string) => string | undefined;
}

/**
 * 文本输入 + DropdownMenu 预设；表格态交互对齐 EditableCell（点击展示层进入编辑）。
 */
export const BudgetUnitInput: React.FC<BudgetUnitInputProps> = ({
  value,
  onChange,
  presets,
  disabled,
  placeholder = '',
  dropdownId,
  variant = 'table',
  translateUnitLabel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const [editing, setEditing] = useState(variant === 'form');
  const [draft, setDraft] = useState(() => toDisplay(value));
  const selectInProgressRef = useRef(false);

  const isTable = variant === 'table';

  useEffect(() => {
    if (isTable && !editing) {
      setDraft(toDisplay(value));
    }
    if (!isTable) {
      setDraft(toDisplay(value));
    }
  }, [value, editing, isTable]);

  const items: DropdownMenuItem[] = presets.map((p) => ({
    id: p,
    label: translateUnitLabel?.(p) ?? p,
  }));

  const updateMenuPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const w = Math.round(rect.width);
    // 与输入框同宽（单位列当前为 40px）；避免此前 Math.max(48, …) 强制宽于列宽
    setMenuWidth(w > 0 ? w : undefined);
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
  }, []);

  const openMenu = useCallback(() => {
    updateMenuPosition();
    setMenuOpen(true);
  }, [updateMenuPosition]);

  const commit = useCallback(() => {
    const next = toStored(draft);
    if (next !== value) {
      onChange(next);
    }
  }, [draft, value, onChange]);

  useLayoutEffect(() => {
    if (!isTable || !editing || disabled) return;
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
      updateMenuPosition();
      setMenuOpen(true);
    }
  }, [isTable, editing, disabled, updateMenuPosition]);

  const beginEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (disabled) return;
    setDraft(toDisplay(value));
    setEditing(true);
  };

  const handleContentClick = (e: React.MouseEvent) => {
    beginEdit(e);
  };

  const handleInputFocus = () => {
    if (disabled) return;
    if (!isTable) {
      setDraft(toDisplay(value));
    }
    openMenu();
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    openMenu();
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      if (selectInProgressRef.current) {
        selectInProgressRef.current = false;
        if (isTable) {
          setEditing(false);
        }
        setMenuOpen(false);
        return;
      }
      commit();
      if (isTable) {
        setEditing(false);
      }
      setMenuOpen(false);
    }, 200);
  };

  const handleSelect = (item: DropdownMenuItem) => {
    selectInProgressRef.current = true;
    const canonical = String(item.id);
    setDraft(canonical);
    onChange(toStored(canonical));
    setMenuOpen(false);
    if (isTable) {
      setEditing(false);
    }
    inputRef.current?.blur();
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onScroll = () => updateMenuPosition();
    window.addEventListener('scroll', onScroll, true);
    const tableContainer = document.querySelector('.budget-table-container');
    tableContainer?.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      tableContainer?.removeEventListener('scroll', onScroll, true);
    };
  }, [menuOpen, updateMenuPosition]);

  const selectedMenuId = presets.some((p) => p === draft) ? draft : null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
      setMenuOpen(false);
      if (isTable) {
        setEditing(false);
      }
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setDraft(toDisplay(value));
      setMenuOpen(false);
      if (isTable) {
        setEditing(false);
      }
      inputRef.current?.blur();
    }
  };

  const inputProps: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'ref'> = {
    type: 'text',
    value: draft,
    disabled,
    placeholder,
    onClick: handleInputClick,
    onFocus: handleInputFocus,
    onChange: (e) => setDraft(e.target.value),
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    autoComplete: 'off',
  };

  if (isTable) {
    if (!editing || disabled) {
      const raw = toDisplay(value);
      const displayText =
        raw === '' ? '' : translateUnitLabel ? translateUnitLabel(raw) ?? raw : raw;
      return (
        <div className="editable-cell-wrapper">
          <div
            className={`editable-cell-content${disabled ? ' disabled' : ''}`}
            onClick={disabled ? undefined : handleContentClick}
          >
            {displayText}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="editable-cell-wrapper">
          <input ref={inputRef} {...inputProps} className="editable-cell-input" />
        </div>
        {menuOpen && !disabled ? (
          <DropdownMenu
            items={items}
            visible={true}
            onClose={() => setMenuOpen(false)}
            onSelect={handleSelect}
            position={menuPos}
            useFixedPosition={true}
            portalToBody={true}
            width={menuWidth}
            selectedValue={selectedMenuId}
            maxHeight={280}
            dataDropdownId={dropdownId}
          />
        ) : null}
      </>
    );
  }

  /* form：由 RelatedBudget .create-form-field input[type="text"] 统一样式，不加自定义 class */
  return (
    <>
      <input ref={inputRef} {...inputProps} />
      {menuOpen && !disabled ? (
        <DropdownMenu
          items={items}
          visible={true}
          onClose={() => setMenuOpen(false)}
          onSelect={handleSelect}
          position={menuPos}
          useFixedPosition={true}
          portalToBody={true}
          width={menuWidth}
          selectedValue={selectedMenuId}
          maxHeight={280}
          dataDropdownId={dropdownId}
        />
      ) : null}
    </>
  );
};
